<template>
  <reports-table
    module="payroll"
    reports-resource="monthly-payroll-reports"
    name="monthly_employee_payroll"
    :filters="serializedFilters"
    :exclude-page-params="true"
    :prefetch="generateFromUrl"
    enable-export
    :excel-builder="buildMonthlyPayrollSheet"
  >
    <template #default="{generateReport}">
      <oxd-table-filter :filter-title="$t('payroll.monthly_payroll_report')">
        <oxd-form
          @submit-valid="onGenerate(generateReport)"
          @reset="onGenerate(generateReport)"
        >
          <oxd-form-row>
            <oxd-grid :cols="4" class="orangehrm-full-width-grid">
              <oxd-grid-item>
                <oxd-input-field
                  v-model="filters.year"
                  type="select"
                  :label="$t('payroll.report_year')"
                  :options="yearOptions"
                  :show-empty-selector="false"
                />
              </oxd-grid-item>
              <oxd-grid-item>
                <oxd-input-field
                  v-model="filters.month"
                  type="select"
                  :label="$t('payroll.report_month')"
                  :options="monthOptions"
                  :show-empty-selector="false"
                />
                <oxd-text class="orangehrm-input-hint" tag="p">
                  {{ $t('payroll.report_month_period_end_hint') }}
                </oxd-text>
              </oxd-grid-item>
              <oxd-grid-item>
                <oxd-input-field
                  v-model="filters.subunit"
                  type="select"
                  :label="$t('general.sub_unit')"
                  :options="subunits"
                />
              </oxd-grid-item>
              <oxd-grid-item>
                <oxd-input-field
                  v-model="filters.locations"
                  :label="$t('general.location')"
                  :options="locations"
                  type="multiselect"
                  value="-- Select --"
                />
              </oxd-grid-item>
            </oxd-grid>
          </oxd-form-row>

          <oxd-divider />

          <oxd-form-actions>
            <required-text />
            <oxd-button
              type="reset"
              display-type="ghost"
              :label="$t('general.reset')"
              @click="onClickReset"
            />
            <oxd-button
              type="submit"
              display-type="secondary"
              class="orangehrm-left-space"
              :label="$t('general.generate')"
            />
          </oxd-form-actions>
        </oxd-form>
      </oxd-table-filter>
      <br />
    </template>
  </reports-table>
</template>

<script>
import {computed, ref} from 'vue';
import ReportsTable from '@/core/components/table/ReportsTable';
import {yearRange} from '@/core/util/helper/year-range';
import {buildMonthlyPayrollSheet} from '@/orangehrmPayrollPlugin/util/monthlyPayrollExcelExport';

const monthOptions = [
  {id: 1, label: 'January'},
  {id: 2, label: 'February'},
  {id: 3, label: 'March'},
  {id: 4, label: 'April'},
  {id: 5, label: 'May'},
  {id: 6, label: 'June'},
  {id: 7, label: 'July'},
  {id: 8, label: 'August'},
  {id: 9, label: 'September'},
  {id: 10, label: 'October'},
  {id: 11, label: 'November'},
  {id: 12, label: 'December'},
];

const buildDefaultFilters = () => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  return {
    year: {id: currentYear, label: String(currentYear)},
    month: monthOptions.find((m) => m.id === currentMonth) ?? monthOptions[0],
    subunit: null,
    locations: [],
  };
};

const parsePositiveInt = (raw) => {
  const value = Number.parseInt(String(raw ?? ''), 10);
  return Number.isInteger(value) && value > 0 ? value : null;
};

const readReportQuery = () => {
  const params = new URLSearchParams(window.location.search);
  const year = parsePositiveInt(params.get('year'));
  const month = parsePositiveInt(params.get('month'));
  const subUnitId = parsePositiveInt(params.get('subUnitId'));
  const locationIds = [
    ...params.getAll('locationIds'),
    ...params.getAll('locationIds[]'),
  ]
    .flatMap((value) => String(value).split(','))
    .map((value) => parsePositiveInt(value))
    .filter((id) => id !== null);

  return {
    year,
    month,
    subUnitId,
    locationIds,
    hasPeriod: year !== null && month !== null && month <= 12,
  };
};

const applyReportQuery = (filters, props, yearOptions) => {
  const query = readReportQuery();
  if (!query.hasPeriod) {
    return false;
  }

  let yearOption = yearOptions.find((option) => option.id === query.year);
  if (!yearOption) {
    yearOption = {id: query.year, label: String(query.year)};
    yearOptions.push(yearOption);
    yearOptions.sort((left, right) => left.id - right.id);
  }

  const monthOption = monthOptions.find((option) => option.id === query.month);
  if (!monthOption) {
    return false;
  }

  filters.value = {
    year: yearOption,
    month: monthOption,
    subunit:
      props.subunits.find((item) => Number(item.id) === query.subUnitId) ??
      null,
    locations: props.locations.filter((item) =>
      query.locationIds.includes(Number(item.id)),
    ),
  };
  return true;
};

const writeReportQuery = (filters) => {
  const year = filters.year?.id;
  const month = filters.month?.id;
  if (!year || !month) {
    return;
  }

  const params = new URLSearchParams(window.location.search);
  params.set('year', String(year));
  params.set('month', String(month));
  if (filters.subunit?.id) {
    params.set('subUnitId', String(filters.subunit.id));
  } else {
    params.delete('subUnitId');
  }

  params.delete('locationIds');
  params.delete('locationIds[]');
  const locationIds = (filters.locations || [])
    .map((item) => item.id)
    .filter((id) => id !== null && id !== undefined && id !== '');
  if (locationIds.length) {
    params.set('locationIds', locationIds.join(','));
  }

  const qs = params.toString();
  const next = `${window.location.pathname}${qs ? `?${qs}` : ''}${
    window.location.hash || ''
  }`;
  window.history.replaceState(window.history.state, '', next);
};

export default {
  components: {
    'reports-table': ReportsTable,
  },
  props: {
    locations: {
      type: Array,
      default: () => [],
    },
    subunits: {
      type: Array,
      default: () => [],
    },
  },

  setup(props) {
    const currentYear = new Date().getFullYear();
    const yearOptions = yearRange(11, currentYear).map((year) => ({
      id: year,
      label: String(year),
    }));

    const filters = ref(buildDefaultFilters());
    const generateFromUrl = applyReportQuery(filters, props, yearOptions);
    if (generateFromUrl) {
      writeReportQuery(filters.value);
    }

    const serializedFilters = computed(() => {
      return {
        year: filters.value.year?.id ?? null,
        month: filters.value.month?.id ?? null,
        subUnitId: filters.value.subunit?.id,
        locationIds: filters.value.locations.map((item) => item.id),
      };
    });

    return {
      filters,
      serializedFilters,
      generateFromUrl,
      yearOptions,
      monthOptions,
      buildMonthlyPayrollSheet,
    };
  },

  methods: {
    onClickReset() {
      this.filters = buildDefaultFilters();
    },
    async onGenerate(generateReport) {
      this.syncFiltersToUrl();
      await generateReport();
    },
    syncFiltersToUrl() {
      writeReportQuery(this.filters);
    },
  },
};
</script>
